// The assistant's library search (#1001, spec docs/specs/editor/assistant-search.md): live's
// `search_gallery_assets`, run by the editor over S3's index as the user, between two answers. A
// plan that searches holds its searches alone (decision 2); the editor runs them, then asks again
// with the files found first in `media` and the message `continueLine` makes. Pure: the search's
// request is `use-assistant.ts`'s.
import { ApiError } from "../../../api/errors";
import type { SearchAssetsParams } from "../../../api/generated/model";
import { toUserMessage } from "../../../platform/messages";

/** A plan's search: the words of what the files show, their kind (null for both) and how many. */
export interface LibrarySearch {
  query: string;
  kind: "image" | "video" | null;
  limit: number;
}

/** A message's searches at most, in its one round (decision 3). */
export const MAX_SEARCHES = 5;

/** Five hits a file asked for, at most S3's page: a video matching in several scenes leaves room. */
const HITS_PER_FILE = 5;
const MAX_HITS = 50;

/** Why a search is not run, or failed, in the panel's words. */
export const SEARCH = {
  again: "This message searched the library already.",
  most: `A message searches the library at most ${MAX_SEARCHES} times.`,
  off: "The library search is off.",
  down: "The library search is not answering. Try again.",
  busy: "Too many searches: try again in a minute.",
} as const;

/** S3's route's params for a search: its words, its kind and enough hits for its files. */
export function searchParams(search: LibrarySearch): SearchAssetsParams {
  return {
    q: search.query,
    ...(search.kind === null ? {} : { kind: search.kind }),
    limit: Math.min(MAX_HITS, search.limit * HITS_PER_FILE),
  };
}

/** A failed search's reason: the route's 404 (the search is off), 503 and 429, else the API's. */
export function searchFailed(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return SEARCH.off;
    if (error.status === 503) return SEARCH.down;
    if (error.status === 429) return SEARCH.busy;
  }
  return toUserMessage(error);
}

function filesOf(count: number): string {
  return count === 0 ? "no file" : count === 1 ? "1 file" : `${count} files`;
}

/**
 * The follow-up's message (decision 4: not saved to the chat): what each search found. The system
 * prompt names its start, "Continue the request above".
 */
export function continueLine(found: readonly { query: string; files: number }[]): string {
  const each = found.map(({ query, files }) => `${filesOf(files)} for "${query}"`).join(" and ");
  const first = found.some(({ files }) => files > 0) ? ", first in MEDIA" : "";
  return `Continue the request above. The library search found ${each}${first}.`;
}
